import axios, { type Method } from 'axios'
import { SdkContext } from '../internal'
import { getApiOrigin } from '../loader'
import type {
  DeliveryStatus,
  Paged,
  Webhook,
  WebhookDelivery,
  WebhookEventCatalog,
  WebhookHistoryItem
} from '@/types/webhooks'

/**
 * The developers-api (F-101 1b) lives apart from the memberfy-api: in dev at
 * `https://developers.memberfy.dev`, in production at
 * `https://developers.memberfy.net`. `VITE_DEVELOPERS_API_URL` wins when set.
 */
export const getDevelopersApiOrigin = (): string => {
  const envUrl = import.meta.env.VITE_DEVELOPERS_API_URL as string | undefined
  if (envUrl) return envUrl.replace(/\/+$/, '')
  return getApiOrigin().endsWith('memberfy.net') ? 'https://developers.memberfy.net' : 'https://developers.memberfy.dev'
}

export interface Envelope<T> {
  success: boolean
  message?: string
  data: T
  pagination?: Paged<unknown>['pagination']
}

/** One call to the developers-api with the main client's session token, `X-CommunityId` and language. */
export async function developersRequest<T>(
  ctx: SdkContext,
  method: Method,
  path: string,
  options: { body?: unknown; query?: Record<string, unknown> } = {}
): Promise<Envelope<T>> {
  const main = await ctx.ready
  const { token, communityId, language } = main.config
  const params = options.query ? Object.fromEntries(Object.entries(options.query).filter(([, v]) => v !== undefined && v !== '')) : undefined
  const response = await axios.request<Envelope<T>>({
    method,
    url: `${getDevelopersApiOrigin()}/api${path}`,
    data: options.body ?? (method === 'GET' || method === 'DELETE' ? undefined : {}),
    params,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(communityId ? { 'X-CommunityId': communityId } : {}),
      ...(language ? { 'Accept-Language': language } : {})
    }
  })
  return response.data
}

/** A paged list in the envelope (`data[key]` + `pagination`). */
export function pagedOf<T>(envelope: Envelope<Record<string, T[]>>, key: string): Paged<T> {
  const items = envelope.data?.[key] || []
  return { items, pagination: envelope.pagination || { page: 1, limit: items.length, total: items.length, totalPages: 1 } }
}

/**
 * Community webhooks. Same session token, `X-CommunityId` and language as
 * the main client; the answer keeps the `{ success, data }` envelope, and an
 * error is axios' (`error.response.status`, `error.response.data`), which is
 * the shape `apiErrorMessage` already reads. Owner and admin only (the
 * developers-api checks the role with the memberfy-api).
 */
export class WebhooksResource {
  constructor(private ctx: SdkContext) {}

  private request<T>(method: Method, path: string, options: { body?: unknown; query?: Record<string, unknown> } = {}): Promise<Envelope<T>> {
    return developersRequest<T>(this.ctx, method, `/webhooks${path}`, options)
  }

  private paged<T>(envelope: Envelope<Record<string, T[]>>, key: string): Paged<T> {
    return pagedOf(envelope, key)
  }

  /** The families, their events and the `ping` (sent by Enviar teste, never subscribed). */
  async events(): Promise<WebhookEventCatalog> {
    return (await this.request<WebhookEventCatalog>('GET', '/events')).data
  }

  /** Whether fixed IPs are offered and which IPs the destination should allow. */
  async fixedIps(): Promise<{ available: boolean; ips: string[] }> {
    return (await this.request<{ available: boolean; ips: string[] }>('GET', '/fixed-ips')).data
  }

  /** Masked URLs, never the secret, and the ceiling per community. */
  async list(): Promise<{ webhooks: Webhook[]; limit: number }> {
    return (await this.request<{ webhooks: Webhook[]; limit: number }>('GET', '')).data
  }

  /** `secret` (`whsec_…`) comes only in this answer. */
  async create(body: { url: string; description?: string; events: string[]; fixedIps?: boolean }): Promise<{ webhook: Webhook; secret: string }> {
    return (await this.request<{ webhook: Webhook; secret: string }>('POST', '', { body })).data
  }

  /** With the whole URL. */
  async get(id: string): Promise<Webhook> {
    return (await this.request<{ webhook: Webhook }>('GET', `/${id}`)).data.webhook
  }

  /**
   * Only what changes. Applies to the events that happen afterwards; what was
   * already on its way keeps its old destination. Never turns a webhook back on.
   */
  async update(id: string, body: { url?: string; description?: string | null; events?: string[]; fixedIps?: boolean }): Promise<Webhook> {
    return (await this.request<{ webhook: Webhook }>('PATCH', `/${id}`, { body })).data.webhook
  }

  async remove(id: string): Promise<void> {
    await this.request('DELETE', `/${id}`)
  }

  /** A signed `ping` now; the delivery says how it went. 409 when the webhook is off. */
  async test(id: string): Promise<WebhookDelivery> {
    return (await this.request<{ delivery: WebhookDelivery }>('POST', `/${id}/test`)).data.delivery
  }

  /** The new secret is valid at once and returned only here. */
  async rotateSecret(id: string): Promise<{ webhook: Webhook; secret: string }> {
    return (await this.request<{ webhook: Webhook; secret: string }>('POST', `/${id}/rotate-secret`)).data
  }

  async disable(id: string): Promise<Webhook> {
    return (await this.request<{ webhook: Webhook }>('POST', `/${id}/disable`)).data.webhook
  }

  /** Pings first: without a 2xx it answers 422 with the ping in `data.delivery`. */
  async enable(id: string): Promise<{ webhook: Webhook; delivery: WebhookDelivery }> {
    return (await this.request<{ webhook: Webhook; delivery: WebhookDelivery }>('POST', `/${id}/enable`)).data
  }

  /** Newest first, kept for 30 days, without the body. */
  async deliveries(id: string, query: { status?: DeliveryStatus; eventType?: string; page?: number; limit?: number } = {}): Promise<Paged<WebhookDelivery>> {
    return this.paged(await this.request<Record<string, WebhookDelivery[]>>('GET', `/${id}/deliveries`, { query }), 'deliveries')
  }

  /** The body sent and every attempt (up to 1 KB of each answer). */
  async delivery(id: string, deliveryId: string): Promise<WebhookDelivery> {
    return (await this.request<{ delivery: WebhookDelivery }>('GET', `/${id}/deliveries/${deliveryId}`)).data.delivery
  }

  /** Same `webhook-id`, a new cycle; the answer carries the first attempt. 409 when off or still pending. */
  async resend(id: string, deliveryId: string): Promise<WebhookDelivery> {
    return (await this.request<{ delivery: WebhookDelivery }>('POST', `/${id}/deliveries/${deliveryId}/resend`)).data.delivery
  }

  /** Who did what on the tab (or on one webhook, with `id`), newest first, kept for 30 days. */
  async history(query: { id?: string; page?: number; limit?: number } = {}): Promise<Paged<WebhookHistoryItem>> {
    const { id, ...rest } = query
    return this.paged(await this.request<Record<string, WebhookHistoryItem[]>>('GET', id ? `/${id}/history` : '/history', { query: rest }), 'history')
  }
}
